import React, { useContext, useEffect, useState } from "react";
import {
  Modal,
  Input,
  Select,
  Button,
  Form,
  Upload,
  message,
  Row,
  Col,
  ColorPicker,
  DatePicker,
} from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
  FileWordOutlined,
  FileImageOutlined,
  FileUnknownOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { RcFile } from "antd/es/upload/interface";
import { MdDeleteOutline } from "react-icons/md";
import { AuthContext } from "../../Auth/AuthContext";
import {
  createLeadStatus,
  deleteLeadStatus,
  getallDeveloperForTask,
  getAllSources,
  getAllTags,
  getLeadStatus,
  getRawDataById,
  updateLeadStatus,
  updateRawData,
} from "../../utils/API";
import moment from "moment";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import countries from "../../utils/country.json";
import { UploadFile } from "antd/lib";
import { UploadChangeParam } from "antd/lib/upload";
import { Typography } from "antd";

dayjs.extend(customParseFormat);

const { Text } = Typography;

const { Option } = Select;

const probabilityInputTextStyle: React.CSSProperties = {
  fontFamily: "Inter, sans-serif",
  fontSize: 16,
  fontWeight: 400,
  color: "#333335",
};
const probabilityInputWrapperStyle: React.CSSProperties = {
  ...probabilityInputTextStyle,
  height: 43,
  border: "1px solid #e9edf6",
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
};
const probabilityInputStyles = {
  affixWrapper: probabilityInputWrapperStyle,
  input: probabilityInputTextStyle,
  suffix: probabilityInputTextStyle,
};

const RAW_DATA_DATE_FORMAT = "DD-MM-YYYY";

const parseRawDataDate = (value: unknown) => {
  if (!value) return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  const stringValue = String(value).trim();
  if (!stringValue || stringValue.toLowerCase() === "invalid date") {
    return null;
  }

  const supportedFormats = [
    RAW_DATA_DATE_FORMAT,
    "D-M-YYYY",
    "YYYY-MM-DD",
    "YYYY-MM-DDTHH:mm:ss.SSSZ",
    "YYYY-MM-DDTHH:mm:ssZ",
  ];

  for (const format of supportedFormats) {
    const parsedDate = dayjs(stringValue, format, true);
    if (parsedDate.isValid()) return parsedDate;
  }

  const parsedDate = dayjs(stringValue);
  return parsedDate.isValid() ? parsedDate : null;
};

interface RawdataFormValues {
  contactName: string;
  phone: string;
  phone_2: string;
  date: string;
  company: string;
  leadSource: string[];
  leadTags: string[];
  leadValue: string;
  probability: string;
  description: string;
  title: string;
  link: string;
  leadowner: string;
  country: string;
  rank: string;
  leadstatus: string;
}

interface EditRawDataModalProps {
  visible: boolean;
  onCancel: () => void;
  onUpdate?: (values: RawdataFormValues) => void;
  rowData?: RawdataFormValues; // Pass row data to edit
  currentRawDataId: any;
}

const currencies = [
  { code: "USD", name: "US Dollar", symbol: "$", rate: 88.73 },
  { code: "EUR", name: "Euro", symbol: "€", rate: 89.73 },
  { code: "GBP", name: "British Pound", symbol: "£", rate: 104.5 },
  { code: "AUD", name: "Australian Dollar", symbol: "A$", rate: 54.8 },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$", rate: 60.45 },
  { code: "SGD", name: "Singapore Dollar", symbol: "S$", rate: 61.8 },
  { code: "AED", name: "UAE Dirham", symbol: "د.إ", rate: 22.65 },
];

const EditRawDataModal: React.FC<EditRawDataModalProps> = ({
  visible,
  onCancel,
  onUpdate,
  rowData,
  currentRawDataId,
}) => {
  const [form] = Form.useForm();
  const { authData } = useContext(AuthContext);
  const IMAGEURL = process.env.REACT_APP_IMAGE_URL + "/rawdata/";
  const [previewImage, setPreviewImage] = useState<string>("");
  const [fileList, setFileList] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const roleId = authData?.roleId;
  const isDeveloper =
    roleId?.roleId === 2 || roleId?.name?.toLowerCase() === "developer";
  const [statuses, setStatuses] = useState<any>([]);
  const [isAddingStatus, setIsAddingStatus] = useState(false);
  const [newStatusName, setNewStatusName] = useState("");
  const [newStatusColor, setNewStatusColor] = useState("#000000");
  const [tags, setTags] = useState<
    Array<{ _id: string; name: string; color: string }>
  >([]);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [sources, setSources] = useState<Array<string>>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [developers, setDevelopers] = useState([]);
  const [editingStatus, setEditingStatus] = useState<any>(null);
  const [platform, setPlatform] = useState([]);
  const [Purposal, setPurposal] = useState<Array<string | File>>([]);
  const [fileListPurposal, setFileListPurposal] = useState<any[]>([]);
  const [Prd, setPrd] = useState<Array<string | File>>([]);
  const [fileListPrd, setFileListPrd] = useState<any[]>([]);
  const [Contract, setContract] = useState<Array<string | File>>([]);
  const [fileListContract, setFileListContract] = useState<any[]>([]);

  const handleCurrencyChange = (currencyCode: string) => {
    const currency = currencies.find((c) => c.code === currencyCode);
    const currentAmount = form.getFieldValue("leadValueInCurrency") || 0;
    const newRate = currency?.rate || 0;
    const convertedValue = currentAmount * newRate;

    form.setFieldsValue({
      leadValueCurrency: currencyCode,
      leadValueInINR: newRate,
      leadValue: Math.round(convertedValue),
    });
  };

  const calculateConvertedValue = () => {
    const amount = form.getFieldValue("leadValueInCurrency") || 0;
    const rate = form.getFieldValue("leadValueInINR") || 0;
    const convertedValue = amount * rate;

    form.setFieldsValue({
      leadValue: Math.round(convertedValue),
    });
  };

  const fetchDevelopers = async () => {
    try {
      const res = await getallDeveloperForTask("", "");
      if (res.data) {
        const owners = res.data.filter((dev: any) => dev.isOwner === true);
        const platform = res.data.filter((dev: any) => dev.isPlatform === true);
        setPlatform(platform);
        setDevelopers(owners);
      }
    } catch (err) {
      console.error("Error fetching developers:", err);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await getAllTags();
      setTags(response.tags || []);
    } catch (error) {
      console.error("Error fetching tags:", error);
      message.error("Failed to fetch tags");
    }
  };

  const fetchSource = async () => {
    try {
      const getSource = await getAllSources(searchText, page);
      if (getSource) {
        let filteredSource = getSource.sources.filter((item: any) =>
          item.category.includes("Sales"),
        );
        setSources(filteredSource);
      }
    } catch (error) {
      console.error("Error fetching source:", error);
    }
  };

  const fetchStatuses = async () => {
    try {
      if (isDeveloper) {
        setStatuses([]);
        return;
      }

      const response = await getLeadStatus();
      setStatuses(response.data);
    } catch (error) {
      console.error("Error fetching statuses:", error);
      message.error("Failed to fetch statuses.");
    }
  };

  const handleAddNewStatus = async () => {
    if (!newStatusName.trim()) {
      message.error("Please enter a status name");
      return;
    }
    try {
      if (editingStatus) {
        const response = await updateLeadStatus(editingStatus._id, {
          name: newStatusName,
          color: newStatusColor,
        });

        if (response.success) {
          message.success("Status updated successfully");
          setStatuses((prev: any) =>
            prev.map((status: any) =>
              status._id === editingStatus._id
                ? { ...status, name: newStatusName, color: newStatusColor }
                : status,
            ),
          );
          await fetchStatuses();
        }
      } else {
        const response = await createLeadStatus({
          name: newStatusName,
          color: newStatusColor,
          type: "Rawdata",
        });

        if (response.success) {
          message.success("Status added successfully");
          setStatuses((prev: any) => [...prev, response.data]);
          await fetchStatuses();
        }
      }

      setIsAddingStatus(false);
      setNewStatusName("");
      setNewStatusColor("#000000");
      setEditingStatus(null);
    } catch (error) {
      console.error("Error handling status:", error);
      message.error(
        editingStatus ? "Failed to update status" : "Failed to add new status",
      );
    }
  };

  const handleEditStatus = (status: any) => {
    setIsAddingStatus(true);
    setEditingStatus(status);
    setNewStatusName(status.name);
    setNewStatusColor(status.color || "#000000");
  };

  const handleDeleteStatus = async (statusId: string) => {
    try {
      await deleteLeadStatus(statusId, {});
      message.success("Status deleted successfully");
      setStatuses(statuses.filter((status: any) => status._id !== statusId));
      if (selectedStatus === statusId) {
        setSelectedStatus("");
        form.setFieldsValue({ leadStatus: undefined });
      }
      await fetchStatuses();
    } catch (error) {
      console.error("Error deleting status:", error);
      message.error("Failed to delete status");
    }
  };

  const fetchRawData = async () => {
    setIsLoading(true);
    try {
      const res = await getRawDataById(currentRawDataId);
      const leadStatusId = res.data.leadstatus?._id;
      const isValidStatus = statuses.some(
        (status: any) => status._id === leadStatusId,
      );

      form.setFieldsValue({
        title: res.data.title,
        email: res.data.email,
        link: res.data.link,
        country: res.data.country,
        phone: res.data.phone,
        phone_2: res.data.phone_2,
        contactName: res.data.contactName,
        company: res.data.company,
        date: parseRawDataDate(res.data.date),
        rank: res.data.rank,
        leadValue: res.data.leadValue,
        probability: res.data.probability ?? 0,
        description: res.data.description,
        leadValueCurrency: res.data.leadValueCurrency,
        leadValueInINR: res.data.leadValueInINR,
        leadValueInCurrency: res.data.leadValueInCurrency,
        leadSource: res.data.leadSource?._id || undefined,
        leadTags: Array.isArray(res.data.leadTags)
          ? res.data.leadTags.map((tag: any) => tag?._id).filter(Boolean)
          : [],
        leadstatus: res.data.leadstatus?._id,
        leadowner: res.data.leadowner?._id,
        leadAssigne: res.data.leadAssigne?._id,
        platform: res?.data?.platform?._id || undefined,
      });

      if (res.data.purposal) {
        const purposal = Array.isArray(res.data.purposal)
          ? res.data.purposal
          : [res.data.purposal];
        setPurposal(purposal);
        setFileListPurposal(
          purposal.map((purposal: string, index: number) => ({
            uid: `existing-${index}-${Date.now()}`,
            name: purposal.split("/").pop() || "purposal",
            status: "done",
            url: `${IMAGEURL}${purposal}`,
            ...(purposal.match(/\.(jpg|jpeg|png|gif|webp)$/i)
              ? { preview: `${IMAGEURL}${purposal}` }
              : {}),
          })),
        );
      }

      if (res.data.prd) {
        const prd = Array.isArray(res.data.prd) ? res.data.prd : [res.data.prd];
        setPrd(prd);
        setFileListPrd(
          prd.map((p: string, index: number) => ({
            uid: `existing-prd-${index}-${Date.now()}`,
            name: p.split("/").pop() || "prd",
            status: "done",
            url: `${IMAGEURL}${p}`,
            ...(p.match(/\.(jpg|jpeg|png|gif|webp)$/i)
              ? { preview: `${IMAGEURL}${p}` }
              : {}),
          })),
        );
      }

      if (res.data.contract) {
        const contract = Array.isArray(res.data.contract)
          ? res.data.contract
          : [res.data.contract];
        setContract(contract);
        setFileListContract(
          contract.map((contract: string, index: number) => ({
            uid: `existing-${index}-${Date.now()}`,
            name: contract.split("/").pop() || "contract",
            status: "done",
            url: `${IMAGEURL}${contract}`,
            ...(contract.match(/\.(jpg|jpeg|png|gif|webp)$/i)
              ? { preview: `${IMAGEURL}${contract}` }
              : {}),
          })),
        );
      }

      setSelectedStatus(isValidStatus ? leadStatusId : "");
    } catch (error) {
      console.error("Error fetching Rawdata:", error);
      message.error("Failed to fetch Rawdata data.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);
      const parsedDate = parseRawDataDate(values.date);

      if (!parsedDate) {
        form.setFields([
          {
            name: "date",
            errors: ["Please select a valid date"],
          },
        ]);
        message.error("Please select a valid date");
        return;
      }

      const formData = new FormData();

      Object.keys(values).forEach((key) => {
        if (
          key !== "purposal" &&
          key !== "prd" &&
          key !== "contract" &&
          key !== "milestones"
        ) {
          if (key === "date") {
            formData.append("date", parsedDate.format(RAW_DATA_DATE_FORMAT));
          } else if (key === "leadValue") {
            const roundedValue =
              values.leadValue !== undefined && values.leadValue !== null
                ? Math.round(values.leadValue)
                : "";
            formData.append("leadValue", roundedValue.toString());
          } else if (Array.isArray(values[key])) {
            formData.append(key, JSON.stringify(values[key]));
          } else {
            formData.append(key, values[key] ?? "");
          }
        }
      });

      if (values.milestones) {
        formData.append("milestones", JSON.stringify(values.milestones));
      }

      formData.delete("purposal");
      formData.delete("purposalExisting");
      if (Purposal.length === 0) {
        formData.append("purposalClear", "true");
      } else {
        Purposal.forEach((file) => {
          if (file instanceof File) {
            formData.append("purposal", file);
          } else {
            formData.append("purposalExisting", file);
          }
        });
      }

      formData.delete("prd");
      formData.delete("prdExisting");
      if (Prd.length === 0) {
        formData.append("prdClear", "true");
      } else {
        Prd.forEach((file) => {
          if (file instanceof File) {
            formData.append("prd", file);
          } else {
            formData.append("prdExisting", file);
          }
        });
      }

      formData.delete("contract");
      formData.delete("contractExisting");
      if (Contract.length === 0) {
        formData.append("contractClear", "true");
      } else {
        Contract.forEach((file) => {
          if (file instanceof File) {
            formData.append("contract", file);
          } else {
            formData.append("contractExisting", file);
          }
        });
      }

      const response = await updateRawData(currentRawDataId, formData);
      message.success("Lead updated successfully!");
      onUpdate?.(response.data);
      onCancel();
    } catch (error: any) {
      message.error(error?.message || "Failed to update Lead!");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getBase64 = (file: File | Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const imageProps = {
    name: "profilePhoto",
    multiple: false,
    beforeUpload: (file: RcFile) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("Only image files allowed!");
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: async (info: any) => {
      let newFileList = [...info.fileList].slice(-1);
      if (newFileList.length > 0) {
        const file = newFileList[0];
        if (!file.url && !file.preview && file.originFileObj) {
          file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview || "");
      } else {
        setPreviewImage("");
      }
      setFileList(newFileList);
    },
    fileList,
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        await fetchStatuses();
        await Promise.all([fetchTags(), fetchSource(), fetchDevelopers()]);
        if (visible) {
          await fetchRawData();
        }
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };

    initializeData();

    if (!visible) {
      form.resetFields();
      setFileList([]);
      setPreviewImage("");
      setSelectedStatus("");
    }
  }, [visible, form]);

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    if (ext === "pdf")
      return <FilePdfOutlined className="text-2xl text-red-500 mb-1" />;
    if (ext === "doc" || ext === "docx")
      return <FileWordOutlined className="text-2xl text-blue-500 mb-1" />;
    if (ext === "jpg" || ext === "jpeg" || ext === "png")
      return <FileImageOutlined className="text-2xl text-green-500 mb-1" />;
    return <FileUnknownOutlined className="text-2xl text-gray-500 mb-1" />;
  };

  const handleDeletePurposal = (doc: string | File, index: number) => {
    const newPurposal = [...Purposal];
    newPurposal.splice(index, 1);
    setPurposal(newPurposal);
    const newFileListPurposal = [...fileListPurposal];
    newFileListPurposal.splice(index, 1);
    setFileListPurposal(newFileListPurposal);
  };

  const handleDeletePrd = (doc: string | File, index: number) => {
    const newPrd = [...Prd];
    newPrd.splice(index, 1);
    setPrd(newPrd);
    const newFileListPrd = [...fileListPrd];
    newFileListPrd.splice(index, 1);
    setFileListPrd(newFileListPrd);
  };

  const handleDeleteContract = (doc: string | File, index: number) => {
    const newContract = [...Contract];
    newContract.splice(index, 1);
    setContract(newContract);
    const newFileListContract = [...fileListContract];
    newFileListContract.splice(index, 1);
    setFileListContract(newFileListContract);
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      destroyOnClose
      className="custom-modal"
      style={{
        top: 20,
        maxHeight: "calc(100vh - 30px)",
        margin: "0 auto",
        position: "relative",
      }}
      bodyStyle={{
        overflowY: "auto",
        fontFamily: "inter, sans-serif",
      }}
      styles={{
        header: {
          padding: "12px",
          position: "sticky",
          top: 0,
          backgroundColor: "#f1f5ffff",
        },
        content: {
          padding: 0,
          overflow: "hidden",
        },
      }}
      title={
        <div
          style={{
            color: "#4b5563",
            fontFamily: "inter, sans-serif",
            fontSize: "20px",
            fontWeight: 500,
          }}
        >
          Edit RawData
        </div>
      }
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 19 }}
        colon={false}
        style={{ margin: 0 }}
      >
        <div className="elements">
          <Form.Item
            name="title"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Title
              </span>
            }
            rules={[{ required: false, message: "Please enter Title" }]}
            required={false}
          >
            <Input
              size="large"
              placeholder="Enter Title"
              className="Allinput"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Email
              </span>
            }
            rules={[{ required: false, message: "Please enter Email" }]}
            required={false}
          >
            <Input
              size="large"
              placeholder="Enter Email"
              className="Allinput"
            />
          </Form.Item>

          <Form.Item
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Date <span className="text-danger">*</span>
              </span>
            }
            name="date"
            required={false}
            rules={[{ required: true, message: "Please select date" }]}
          >
            <DatePicker
              className="Allinput"
              format="DD-MM-YYYY"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            name="link"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Link <span className="text-danger">*</span>
              </span>
            }
            rules={[{ required: true, message: "Please enter Link" }]}
          >
            <Input size="large" placeholder="Enter Link" className="Allinput" />
          </Form.Item>

          <Form.Item
            name="leadowner"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Lead Owner <span className="text-danger">*</span>
              </span>
            }
            rules={[{ required: true, message: "Please enter Lead Owner" }]}
          >
            <Select placeholder="Owner" className="CustomSelect">
              {developers.map((dev: any) => (
                <Select.Option key={dev._id} value={dev._id}>
                  {dev.username}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="platform"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Source Platform ID<span className="text-danger">*</span>
              </span>
            }
            rules={[
              { required: true, message: "Please enter Source Platform ID" },
            ]}
          >
            <Select placeholder="Source Platform ID" className="CustomSelect">
              {platform.map((pla: any) => (
                <Select.Option key={pla._id} value={pla._id}>
                  {pla.username}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="leadAssigne"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Lead Assignee
              </span>
            }
            rules={[{ required: true, message: "Please Select Lead Assignee" }]}
          >
            <Select placeholder="Select Lead Assignee" className="CustomSelect">
              {developers.map((dev: any) => (
                <Select.Option key={dev._id} value={dev._id}>
                  {dev.username}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="contactName"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Contact Name <span className="text-danger">*</span>
              </span>
            }
            rules={[{ required: true, message: "Please enter contact name" }]}
            required={false}
          >
            <Input
              size="large"
              placeholder="Enter Contact Name"
              className="Allinput"
            />
          </Form.Item>

          <Form.Item
            name="phone"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Phone No
              </span>
            }
            rules={[
              { required: false, message: "Please enter phone number" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const cleaned = value.replace(/[^\d+]/g, "");
                  const isValid = /^\+?[1-9]\d{7,14}$/.test(cleaned);
                  if (isValid) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject(
                      new Error("Please enter a valid phone number"),
                    );
                  }
                },
              },
            ]}
          >
            <Input
              type="tel"
              size="large"
              placeholder="Enter Phone No"
              className="Allinput"
            />
          </Form.Item>

          <Form.Item
            name="phone_2"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Phone No 2
              </span>
            }
            rules={[
              { required: false, message: "Please enter phone number 2" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const cleaned = value.replace(/[^\d+]/g, "");
                  const isValid = /^\+?[1-9]\d{7,14}$/.test(cleaned);
                  if (isValid) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject(
                      new Error("Please enter a valid phone number"),
                    );
                  }
                },
              },
            ]}
          >
            <Input
              type="tel"
              size="large"
              placeholder="Enter Phone No"
              className="Allinput"
            />
          </Form.Item>

          <Form.Item
            name="company"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Company Name
              </span>
            }
            rules={[{ required: false, message: "Please enter company name" }]}
          >
            <Input
              size="large"
              placeholder="Enter Company Name"
              className="Allinput"
            />
          </Form.Item>

          {/* Lead Value Section - Fully Form Controlled */}
          <Form.Item
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Lead Value
              </span>
            }
            required={false}
            rules={[{ required: false, message: "Please enter lead value" }]}
          >
            <div className="flex flex-col m-0 gap-2">
              <div className="flex gap-2 m-0">
                <Form.Item
                  name="leadValueCurrency"
                  style={{ margin: 0, flexShrink: 0 }}
                >
                  <Select
                    className="CustomSelect"
                    onChange={handleCurrencyChange}
                    style={{ width: 100 }}
                    size="large"
                  >
                    {currencies.map((currency) => (
                      <Option key={currency.code} value={currency.code}>
                        {currency.code}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="leadValueInCurrency"
                  style={{ margin: 0, flex: 1 }}
                >
                  <Input
                    size="large"
                    className="border-1 border-gray-200 rounded-md"
                    type="number"
                    placeholder="Amount"
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      calculateConvertedValue();
                    }}
                  />
                </Form.Item>

                <Form.Item name="leadValueInINR" style={{ margin: 0, flex: 1 }}>
                  <Input
                    size="large"
                    className="border-1 border-gray-200 rounded-md"
                    type="number"
                    placeholder="Exchange Rate"
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      calculateConvertedValue();
                    }}
                  />
                </Form.Item>
              </div>

              <Form.Item name="leadValue" style={{ margin: 0 }}>
                <Input
                  size="large"
                  type="number"
                  prefix="₹"
                  className="border-1 border-gray-200 rounded-md"
                  readOnly
                />
              </Form.Item>
            </div>
          </Form.Item>

          <Form.Item
            name="probability"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Probability
              </span>
            }
          >
            <Input
              size="large"
              placeholder="Enter Probability"
              type="number"
              min={0}
              max={100}
              suffix="%"
              className="Allinput probabilityInput"
              style={probabilityInputWrapperStyle}
              styles={probabilityInputStyles}
            />
          </Form.Item>
          <Form.Item
            name="country"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Country
              </span>
            }
            required={false}
            rules={[{ required: false, message: "Please select a country" }]}
          >
            <Select
              size="large"
              bordered={false}
              placeholder="Select Country"
              showSearch
              optionFilterProp="children"
            >
              {countries.map((c) => (
                <Select.Option key={c.code} value={c.name}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="rank"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Rank
              </span>
            }
          >
            <Input
              size="large"
              placeholder="Enter Rank"
              className="Allinput"
              defaultValue={0}
            />
          </Form.Item>

          <Form.Item
            name="leadstatus"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Lead Status <span className="text-danger">*</span>
              </span>
            }
            required={false}
            rules={[{ required: true, message: "Please select the status" }]}
          >
            <Select
              size="large"
              value={selectedStatus}
              onChange={(value) => {
                setSelectedStatus(value);
                form.setFieldsValue({ leadstatus: value });
              }}
              placeholder="Select Lead Status"
              className="CustomSelect"
              open={isStatusDropdownOpen}
              onDropdownVisibleChange={(open) => setIsStatusDropdownOpen(open)}
              dropdownRender={(menu) => (
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                    {statuses.map((status: any) => (
                      <div
                        key={status._id}
                        className="status-item"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "5px 12px",
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedStatus(status._id);
                          form.setFieldsValue({ leadstatus: status._id });
                          setIsStatusDropdownOpen(false);
                        }}
                      >
                        <span
                          style={{ flex: 1, color: status.color || "#000000" }}
                        >
                          {status.name}
                        </span>
                        <FaPen
                          onClick={(e: any) => {
                            e.stopPropagation();
                            handleEditStatus(status);
                          }}
                          className="group-hover:scale-110 text-indigo-500 transition-transform duration-200 mr-2"
                          size={12}
                        />
                        <FaTrashAlt
                          onClick={(e: any) => {
                            e.stopPropagation();
                            handleDeleteStatus(status._id);
                          }}
                          className="group-hover:scale-110 text-red-500 transition-transform"
                          size={12}
                        />
                      </div>
                    ))}
                  </div>

                  <div
                    style={{
                      position: "sticky",
                      bottom: 0,
                      background: "#fff",
                      padding: "8px",
                      borderTop: "1px solid #f0f0f0",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {isAddingStatus ? (
                      <>
                        <div className="flex">
                          <Input
                            placeholder={
                              editingStatus
                                ? "Edit status name"
                                : "Enter new status name"
                            }
                            value={newStatusName}
                            onChange={(e) => setNewStatusName(e.target.value)}
                            style={{
                              marginBottom: "8px",
                              width: "90%",
                              height: "35px",
                              borderColor: "#d9d9d9",
                              borderRadius: "5px",
                            }}
                          />
                          <ColorPicker
                            size="small"
                            value={newStatusColor}
                            onChange={(color) =>
                              setNewStatusColor(color.toHexString())
                            }
                            onChangeComplete={(color) =>
                              setNewStatusColor(color.toHexString())
                            }
                            getPopupContainer={(trigger: any) =>
                              trigger.parentElement
                            }
                            style={{ margin: "auto", padding: "5px" }}
                          />
                        </div>
                        <div className="flex w-full justify-end gap-2">
                          <Button
                            size="small"
                            onClick={() => {
                              setIsAddingStatus(false);
                              setNewStatusName("");
                              setNewStatusColor("#000000");
                              setEditingStatus(null);
                            }}
                            style={{
                              width: "140px",
                              height: "35px",
                              borderRadius: "4px",
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            onClick={handleAddNewStatus}
                            style={{
                              width: "140px",
                              height: "35px",
                              backgroundColor: "#001529",
                              color: "#ffffff",
                            }}
                          >
                            {editingStatus ? "Update" : "Save"}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div
                        style={{
                          padding: "4px",
                          width: "100%",
                          display: "flex",
                          justifyContent: "end",
                        }}
                      >
                        <Button
                          icon={<PlusOutlined />}
                          onClick={() => setIsAddingStatus(true)}
                          style={{
                            backgroundColor: "#001529",
                            color: "#ffffff",
                            border: "none",
                          }}
                        >
                          Add New Status
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            >
              {!selectedStatus && (
                <Select.Option key="none" value="" disabled>
                  Unknown Status
                </Select.Option>
              )}
              {statuses.map((status: any) => (
                <Select.Option key={status._id} value={status._id}>
                  <span style={{ color: status.color || "#000000" }}>
                    {status.name}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="leadSource"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Lead Source <span className="text-danger">*</span>
              </span>
            }
            rules={[
              {
                required: true,
                message: "Please select at least one lead source",
              },
            ]}
          >
            <Select
              allowClear
              size="large"
              placeholder="Select Lead Source"
              className="CustomSelect"
            >
              {sources.map((item: any, index) => (
                <Option key={index} value={item._id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="leadTags"
            required={false}
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Tags <span className="text-danger">*</span>
              </span>
            }
            rules={[{ required: true, message: "Please select tags" }]}
          >
            <Select
              allowClear
              mode="multiple"
              size="large"
              placeholder="Select Tags"
              className="CustomSelect"
              showSearch
              filterOption={(input, option) =>
                (option?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={tags.map((tag) => ({
                label: tag.name,
                value: tag._id,
                color: tag.color,
              }))}
              optionRender={(option) => (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span style={{ color: option.data.color }}>
                    {option.data.label}
                  </span>
                </div>
              )}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Description
              </span>
            }
            required={false}
            rules={[{ required: false, message: "Please enter description" }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Enter description..."
              className="textarea-input"
              style={{ resize: "none" }}
            />
          </Form.Item>

          <Col span={24}>
            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  Proposal
                </span>
              }
              name="purposal"
              valuePropName="fileList"
              getValueFromEvent={(e) => e.fileList}
            >
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 mb-2">
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Allowed files: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, WEBP
                    (Max 10MB)
                  </Text>
                </div>
                <div className="grid grid-cols-[auto,1fr] gap-4 items-start">
                  <Upload
                    action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
                    listType="picture-card"
                    multiple={true}
                    fileList={fileListPurposal}
                    showUploadList={false}
                    beforeUpload={(file: RcFile) => {
                      // Check file size
                      if (file.size > 10 * 1024 * 1024) {
                        // 10MB
                        message.error(
                          `${file.name} is too large. Maximum file size is 10MB.`,
                        );
                        return Upload.LIST_IGNORE;
                      }

                      // Check file type
                      const allowedTypes = [
                        "application/pdf",
                        "application/msword",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                        "image/jpeg",
                        "image/jpg",
                        "image/png",
                        "image/gif",
                        "image/webp",
                      ];
                      const allowedExtensions = [
                        ".pdf",
                        ".doc",
                        ".docx",
                        ".jpg",
                        ".jpeg",
                        ".png",
                        ".gif",
                        ".webp",
                      ];

                      const hasAllowedType = allowedTypes.includes(file.type);
                      const hasAllowedExtension = allowedExtensions.some(
                        (ext) => file.name.toLowerCase().endsWith(ext),
                      );

                      if (!hasAllowedType && !hasAllowedExtension) {
                        message.error(
                          `${file.name} is not a supported file type. Allowed types: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, WEBP`,
                        );
                        return Upload.LIST_IGNORE;
                      }

                      return false; // Prevent auto upload
                    }}
                    onChange={(info: UploadChangeParam<UploadFile<RcFile>>) => {
                      const newRawFiles = info.fileList
                        .map((f) => f.originFileObj)
                        .filter((f): f is RcFile => !!f);

                      setPurposal((prev) => {
                        const prevNames = new Set(
                          prev.map((f) => (f instanceof File ? f.name : f)),
                        );
                        // Only add files that are not already in state
                        const filteredNew = newRawFiles.filter(
                          (f) => !prevNames.has(f.name),
                        );
                        return [...prev, ...filteredNew];
                      });

                      setFileListPurposal((prev) => {
                        const prevNames = new Set(prev.map((f) => f.name));
                        const filteredNew = info.fileList.filter(
                          (f) => !prevNames.has(f.name),
                        );
                        return [...prev, ...filteredNew];
                      });
                    }}
                  >
                    <Button icon={<UploadOutlined />}>Upload</Button>
                  </Upload>

                  <div className="grid grid-cols-1 gap-3">
                    {Purposal.map((doc, index) => {
                      if (!doc) return null;

                      const fileName =
                        typeof doc === "string"
                          ? doc.split("/").pop() || ""
                          : (doc as File).name;

                      const fileUrl =
                        typeof doc === "string"
                          ? `${IMAGEURL}${doc}`
                          : URL.createObjectURL(doc as File);

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between border border-gray-200 rounded-lg p-2 bg-white shadow-sm"
                        >
                          <div className="flex items-center gap-2 overflow-hidden">
                            {getFileIcon(fileName)}
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium truncate max-w-[250px] hover:underline"
                            >
                              {fileName}
                            </a>
                          </div>

                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeletePurposal(doc, index);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <DeleteOutlined />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  PRD (Project Requirement Document)
                </span>
              }
              name="prd"
              valuePropName="fileList"
              getValueFromEvent={(e) => e.fileList}
            >
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 mb-2">
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Allowed files: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, WEBP
                    (Max 10MB)
                  </Text>
                </div>
                <div className="grid grid-cols-[auto,1fr] gap-4 items-start">
                  <Upload
                    action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
                    listType="picture-card"
                    multiple={true}
                    fileList={fileListPrd}
                    showUploadList={false}
                    beforeUpload={(file: RcFile) => {
                      if (file.size > 10 * 1024 * 1024) {
                        message.error(
                          `${file.name} is too large. Maximum file size is 10MB.`,
                        );
                        return Upload.LIST_IGNORE;
                      }

                      const allowedTypes = [
                        "application/pdf",
                        "application/msword",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                        "image/jpeg",
                        "image/jpg",
                        "image/png",
                        "image/gif",
                        "image/webp",
                      ];
                      const allowedExtensions = [
                        ".pdf",
                        ".doc",
                        ".docx",
                        ".jpg",
                        ".jpeg",
                        ".png",
                        ".gif",
                        ".webp",
                      ];

                      const hasAllowedType = allowedTypes.includes(file.type);
                      const hasAllowedExtension = allowedExtensions.some(
                        (ext) => file.name.toLowerCase().endsWith(ext),
                      );

                      if (!hasAllowedType && !hasAllowedExtension) {
                        message.error(
                          `${file.name} is not a supported file type. Allowed types: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, WEBP`,
                        );
                        return Upload.LIST_IGNORE;
                      }

                      return false;
                    }}
                    onChange={(info: UploadChangeParam<UploadFile<RcFile>>) => {
                      const newRawFiles = info.fileList
                        .map((f) => f.originFileObj)
                        .filter((f): f is RcFile => !!f);

                      setPrd((prev) => {
                        const prevNames = new Set(
                          prev.map((f) => (f instanceof File ? f.name : f)),
                        );
                        const filteredNew = newRawFiles.filter(
                          (f) => !prevNames.has(f.name),
                        );
                        return [...prev, ...filteredNew];
                      });

                      setFileListPrd((prev) => {
                        const prevNames = new Set(prev.map((f) => f.name));
                        const filteredNew = info.fileList.filter(
                          (f) => !prevNames.has(f.name),
                        );
                        return [...prev, ...filteredNew];
                      });
                    }}
                  >
                    <Button icon={<UploadOutlined />}>Upload</Button>
                  </Upload>

                  <div className="grid grid-cols-1 gap-3">
                    {Prd.map((doc, index) => {
                      if (!doc) return null;

                      const fileName =
                        typeof doc === "string"
                          ? doc.split("/").pop() || ""
                          : (doc as File).name;

                      const fileUrl =
                        typeof doc === "string"
                          ? `${IMAGEURL}${doc}`
                          : URL.createObjectURL(doc as File);

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between border border-gray-200 rounded-lg p-2 bg-white shadow-sm"
                        >
                          <div className="flex items-center gap-2 overflow-hidden">
                            {getFileIcon(fileName)}
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium truncate max-w-[250px] hover:underline"
                            >
                              {fileName}
                            </a>
                          </div>

                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeletePrd(doc, index);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <DeleteOutlined />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  NDA/Contract
                </span>
              }
              name="contract"
              valuePropName="fileList"
              getValueFromEvent={(e) => e.fileList}
            >
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 mb-2">
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Allowed files: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, WEBP
                    (Max 10MB)
                  </Text>
                </div>
                <div className="grid grid-cols-[auto,1fr] gap-4 items-start">
                  <Upload
                    listType="picture-card"
                    showUploadList={false}
                    multiple
                    fileList={fileListContract}
                    beforeUpload={(file: RcFile) => {
                      // Check file size
                      if (file.size > 10 * 1024 * 1024) {
                        // 10MB
                        message.error(
                          `${file.name} is too large. Maximum file size is 10MB.`,
                        );
                        return Upload.LIST_IGNORE;
                      }

                      // Check file type
                      const allowedTypes = [
                        "application/pdf",
                        "application/msword",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                        "image/jpeg",
                        "image/jpg",
                        "image/png",
                        "image/gif",
                        "image/webp",
                      ];
                      const allowedExtensions = [
                        ".pdf",
                        ".doc",
                        ".docx",
                        ".jpg",
                        ".jpeg",
                        ".png",
                        ".gif",
                        ".webp",
                      ];

                      const hasAllowedType = allowedTypes.includes(file.type);
                      const hasAllowedExtension = allowedExtensions.some(
                        (ext) => file.name.toLowerCase().endsWith(ext),
                      );

                      if (!hasAllowedType && !hasAllowedExtension) {
                        message.error(
                          `${file.name} is not a supported file type. Allowed types: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, WEBP`,
                        );
                        return Upload.LIST_IGNORE;
                      }

                      return false; // Prevent auto upload
                    }}
                    onChange={(info: UploadChangeParam<UploadFile<RcFile>>) => {
                      const newRawFiles = info.fileList
                        .map((f) => f.originFileObj)
                        .filter((f): f is RcFile => !!f);

                      setContract((prev) => {
                        const prevNames = new Set(
                          prev.map((f) => (f instanceof File ? f.name : f)),
                        );
                        const filteredNew = newRawFiles.filter(
                          (f) => !prevNames.has(f.name),
                        );
                        return [...prev, ...filteredNew];
                      });

                      setFileListContract((prev) => {
                        const prevNames = new Set(prev.map((f) => f.name));
                        const filteredNew = info.fileList.filter(
                          (f) => !prevNames.has(f.name),
                        );
                        return [...prev, ...filteredNew];
                      });
                    }}
                  >
                    <Button icon={<UploadOutlined />}>Upload</Button>
                  </Upload>

                  <div className="grid grid-cols-1 gap-3">
                    {Contract.map((doc, index) => {
                      if (!doc) return null;

                      const fileName =
                        typeof doc === "string"
                          ? doc.split("/").pop() || ""
                          : (doc as File).name;

                      const fileUrl =
                        typeof doc === "string"
                          ? `${IMAGEURL}${doc}`
                          : URL.createObjectURL(doc as File);

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between border border-gray-200 rounded-lg p-2 bg-white shadow-sm"
                        >
                          <div className="flex items-center gap-2 overflow-hidden">
                            {getFileIcon(fileName)}
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium truncate max-w-[250px] hover:underline"
                            >
                              {fileName}
                            </a>
                          </div>

                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteContract(doc, index);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <DeleteOutlined />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Form.Item>
          </Col>
        </div>

        <Row justify="end">
          <Col
            xs={24}
            sm={24}
            md={24}
            xl={24}
            xxl={20}
            className="d-flex justify-content-end me-3"
          >
            <div className="button-wrapper">
              <button
                className="allcanclebutton"
                onClick={onCancel}
                disabled={isLoading}
                type="button"
              >
                Cancel
              </button>
              <button
                className="allsubmitebutton"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default EditRawDataModal;
